import { generatePath } from "react-router-dom"
import queryString from "query-string"
import {
  EACT_STUDY_HALL_COURSE_NAME,
  ACT_STUDY_HALL_COURSE_NAME,
  SAT_STUDY_HALL_COURSE_NAME,
  PSAT_STUDY_HALL_COURSE_NAME,
  STAAR_STUDY_HALL_COURSE_NAME,
  EACT_MASTERY_COURSE_NAME,
  ACT_MASTERY_COURSE_NAME,
  SAT_MASTERY_COURSE_NAME,
  PSAT_MASTERY_COURSE_NAME,
  DSAT_MASTERY_COURSE_NAME,
  STAAR_MASTERY_COURSE_NAME,
  TSIA2_MASTERY_COURSE_NAME,
  Roles
} from "../../../mp-utils/generic-utils"
import uniqBy from "lodash/uniqBy"
import orderBy from "lodash/orderBy"
import pick from "lodash/pick"
import { getLicensedNavs as getLicensedExternalNavs } from "./elements.nav"
// @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
import { getManageUsersSidebarLinks } from "./manageSectionLinks"
import { getSchoolYear } from "../../../mp-utils/generic-utils"
import { getProgramByLicenseName } from "../getProgramByLicenseName"

export default function getMainNavLinks({
  user,
  licenses = [],
  location,
  assessmentSets,
  schools = {},
  educators = {},
  params,
  currentProgram,
  checkProgramType
}) {
  const role = user.role
  const qs = queryString.parse(location.search)

  let filterGUA = user.id

  if (
    role === Roles.District_Admin ||
    role === Roles.ACT_Admin ||
    role === Roles.ACT_Multi_District_Admin
  ) {
    filterGUA = user.schoolDistrict
  }
  if (role === Roles.School_Admin) {
    filterGUA = user.school
  }
  filterGUA = qs.gua ?? filterGUA

  const queryObj = {
    ...qs,
    gua: filterGUA
  }

  const searchFiltersObj = pick(queryObj, [
    "gua",
    "district",
    "school",
    "teacher",
    "class",
    "s"
  ])

  if (user.role === Roles.Student) {
    delete queryObj.gua
  }

  const products = []

  const studyPlanLicenses = licenses.filter(
    license => license.navCategory === "Study Plan" && !license.isExpired
  )
  const studyPlanProgramLinks = studyPlanLicenses.map(license => {
    const program = license.navName
      .replace(license.navCategory, "")
      .trim()
      .toLowerCase()
    return {
      name: license.navName,
      url: "",
      key: program
    }
  })

  if (studyPlanLicenses.length > 0) {
    const currentProgramLicense = studyPlanLicenses.find(
      license =>
        license.navName
          .replace(license.navCategory, "")
          .trim()
          .toLowerCase() === currentProgram
    )
    const defaultProgram = studyPlanLicenses[0].navName
      .replace(studyPlanLicenses[0].navCategory, "")
      .trim()
      .toLowerCase()
    const program = currentProgramLicense ? currentProgram : defaultProgram
    const studyPlanLinks = {
      name: "Study Plan",
      key: "eoc",
      url: `/eoc/${program}/dashboard`,
      hideChildName: true,
      children: [
        { name: "Home", key: "dashboard", url: "/eoc/:program/dashboard" },
        ...(user.role !== Roles.Student
          ? [
              {
                name: "Study Plans",
                key: "study-plans",
                url: "/eoc/:program/study-plans"
              }
            ]
          : []),
        { name: "Insights", key: "insights", url: "/eoc/:program/insights" }
      ].map(item => ({
        ...item,
        url: generatePath(item.url, { program }),
        children: studyPlanProgramLinks.map(link => ({
          ...link,
          url: generatePath(item.url, { program: link.key })
        }))
      }))
    }

    products.push(studyPlanLinks)
  }

  const truScoreProducts = {
    name: "TruScore",
    url: `/truscore?${queryString.stringify(queryObj)}`,
    key: "truscore",
    children: []
  }

  const truscoreTesting = licenses
    .filter(
      license =>
        license?.navCategory?.includes("TruScore Testing") && !license.isExpired
    )
    .map(license => {
      const isBubbleSheet = license.name.includes("Bubble Sheet")
      const program = getProgramByLicenseName(license.name)

      const defaultAssessmentURL = getAssessmentLink(
        params,
        license.licenseSet,
        queryObj,
        assessmentSets,
        isBubbleSheet,
        program,
        checkProgramType
      )
      let testId = license.licenseSet
      if (
        user.role === Roles.Super_Administrator ||
        user.role === Roles.Sales_Rep
      ) {
        testId = license.id
      }
      return {
        name: license.navName,
        id: testId,
        url: defaultAssessmentURL,
        key: program
      }
    })
    .filter(truScorePracticeTest => {
      // Take only truscore licenses without study plan license.
      const hasStudyPlanLicense = studyPlanProgramLinks.find(
        link => link.key === truScorePracticeTest.key
      )
      return !hasStudyPlanLicense
    })

  const truScorePracticeTests = truscoreTesting.filter(
    license => !license?.name?.includes("Bubble Sheet")
  )

  if (truScorePracticeTests.length) {
    const practiceTestTab = {
      name: "Practice Tests",
      url:
        truScorePracticeTests.find(link => link.key === currentProgram)?.url ??
        truScorePracticeTests[0].url ??
        `/truscore/practice-tests?${queryString.stringify(queryObj)}`,
      key: "practice-tests",
      children: truScorePracticeTests
    }
    truScoreProducts.children.push(practiceTestTab)
  }

  const truScoreBubbleSheetTests = truscoreTesting.filter(license =>
    license?.name?.includes("Bubble Sheet")
  )

  if (truScoreBubbleSheetTests.length) {
    const bubbleSheetsTab = {
      name: "Bubble Sheets",
      url:
        truScoreBubbleSheetTests.find(link => link.key === currentProgram)
          ?.url ??
        truScoreBubbleSheetTests[0].url ??
        `/truscore/bubblesheets?${queryString.stringify(queryObj)}`,
      key: "bubblesheets",
      children: truScoreBubbleSheetTests
    }
    truScoreProducts.children.push(bubbleSheetsTab)
  }

  const truScoreStudyHall = licenses
    .filter(
      license =>
        license?.navCategory?.includes("Study Hall") && !license.isExpired
    )
    .map(license => {
      const name =
        license.navName === "Study Hall" ? "ACT Study Hall" : license.navName
      let program = ""
      let courseName = ""
      let url = ""

      if (name.startsWith("ACT")) {
        program = "act"
        courseName = ACT_STUDY_HALL_COURSE_NAME
      }
      if (name.startsWith("EACT")) {
        program = "eact"
        courseName = EACT_STUDY_HALL_COURSE_NAME
      }
      if (name.startsWith("SAT")) {
        program = "sat"
        courseName = SAT_STUDY_HALL_COURSE_NAME
      }
      if (name.startsWith("PSAT")) {
        program = "psat"
        courseName = PSAT_STUDY_HALL_COURSE_NAME
      }
      if (name.startsWith("STAAR")) {
        program = "staar"
        courseName = STAAR_STUDY_HALL_COURSE_NAME
      }

      if (program) {
        url = `/truscore/study-hall/${program}/lessons`
      }

      if (Object.keys(queryObj).length) {
        url = `${url}?${queryString.stringify(queryObj)}`
      }

      return {
        name,
        url: url,
        key: program
      }
    })

  if (truScoreStudyHall.length) {
    const studyHallTab = {
      name: "Study Hall",
      url:
        truScoreStudyHall.find(link => link.key === currentProgram)?.url ??
        truScoreStudyHall[0].url ??
        `/truscore/study-hall?${queryString.stringify(searchFiltersObj)}`,
      key: "study-hall",
      children: truScoreStudyHall
    }
    truScoreProducts.children.push(studyHallTab)
  }

  if (truScoreProducts.children.length) {
    products.push(truScoreProducts)
  }

  const mastery = {
    name: "Mastery Curriculum",
    url: `/mastery?${queryString.stringify(searchFiltersObj)}`,
    key: "mastery",
    hideChildName: true,
    children: []
  }

  const masteryResourcesLinks = licenses
    .filter(
      license =>
        (license?.navCategory?.includes("ACT Resources") ||
          license?.navCategory?.includes("Mastery Resources")) &&
        !license.isExpired
    )
    .map(license => {
      const name = license.navName
      let program = ""
      let courseName = ""
      let url = ""

      if (name.startsWith("ACT")) {
        program = "act"
        courseName = ACT_MASTERY_COURSE_NAME
      }
      if (name.startsWith("SAT")) {
        program = "sat"
        courseName = SAT_MASTERY_COURSE_NAME
      }
      if (name.startsWith("Mastery for the Digital SAT")) {
        program = "dsat"
        courseName = DSAT_MASTERY_COURSE_NAME
      }
      if (name.startsWith("PSAT")) {
        program = "psat"
        courseName = PSAT_MASTERY_COURSE_NAME
      }
      if (name.startsWith("STAAR")) {
        program = "staar"
        courseName = STAAR_MASTERY_COURSE_NAME
      }
      if (name.startsWith("TSIA")) {
        program = "tsia2"
        courseName = TSIA2_MASTERY_COURSE_NAME
      }
      if (name.startsWith("EACT")) {
        program = "eact"
        courseName = EACT_MASTERY_COURSE_NAME
      }

      if (program) {
        url = `/mastery/resources/${program}/lessons`
      }

      if (Object.keys(queryObj).length) {
        url = `${url}?${queryString.stringify(queryObj)}`
      }

      return {
        name,
        url: url,
        key: program
      }
    })

  if (masteryResourcesLinks.length) {
    let url = "/mastery/resources"
    if (Object.keys(searchFiltersObj).length)
      url = `${url}?${queryString.stringify(searchFiltersObj)}`
    const masteryResourcesTab = {
      name: "Home",
      url:
        masteryResourcesLinks.find(link => link.key === currentProgram)?.url ??
        masteryResourcesLinks[0].url ??
        url,
      key: "resources",
      children: masteryResourcesLinks
    }
    mastery.children.push(masteryResourcesTab)
  }

  if (mastery.children.length) {
    products.push(mastery)
  }

  const elements = {
    name: "Elements",
    url: "",
    key: "teacher",
    children: []
  }

  const elementLinks = licenses
    .filter(license => {
      const isElementsLicense = license.navCategory === "elements"

      if (
        Roles.Sales_Rep !== user.role &&
        Roles.Super_Administrator !== user.role &&
        isElementsLicense
      ) {
        return !license.isExpired && license.endDate > Date.now()
      }
      return isElementsLicense
    })
    .sort((a, b) => {
      return a.position - b.position
    })
    .reduce((arr, elem) => {
      return [...arr, ...getLicensedExternalNavs(elem.name)]
    }, [])

  if (elementLinks.length) {
    // Set first user available license as main link to SnapCourse Elements
    elements.url =
      elementLinks.find(link => link.key === currentProgram)?.url ??
      elementLinks[0].url
    elements.children.push({
      name: "Daily Practice",
      url: "",
      key: "elements",
      children: elementLinks
    })

    products.push(elements)
  }

  const snapLicenses = licenses.filter(
    license => license.navCategory === "snap" && !license.isExpired
  )

  if (snapLicenses.length && !location.pathname.includes("masterylessons")) {
    const snapProgramLinks = snapLicenses.map(license => {
      const name = license.navName === "Snap" ? "Snap ACT" : license.navName
      let program = ""
      let url = ""

      if (name === "Snap ACT") {
        program = "act"
      }
      if (license.name.includes("ACT")) {
        program = "act"
      }
      if (license.name.includes("SAT")) {
        program = "sat"
      }
      if (license.name.includes("PSAT")) {
        program = "psat"
      }
      if (license.name.includes("STAAR")) {
        program = "staar"
      }
      if (license.name.includes("TSIA")) {
        program = "tsia2"
      }

      if (program) {
        if (user.role === Roles.Student) {
          url = `/snap/student/${program}`
        } else {
          url = `/snap/reports/${program}`
        }
      }

      if (Object.keys(queryObj).length) {
        url = `${url}?${queryString.stringify(queryObj)}`
      }
      return {
        name,
        url: url,
        key: program
      }
    })

    if (snapProgramLinks.length > 0) {
      const currentProgramSnapUrl =
        snapProgramLinks.find(link => link.key === currentProgram)?.url ??
        snapProgramLinks[0].url

      if (user.role === Roles.Student) {
        const snapStudent = {
          name: "SnapCourse",
          url: currentProgramSnapUrl,
          children: [
            {
              name: "",
              url: "",
              key: "student",
              children: snapProgramLinks
            }
          ],
          key: "snap"
        }

        products.push(snapStudent)
      } else {
        const snapReportsTab = {
          name: "SnapCourse Reports",
          key: "snap",
          url: currentProgramSnapUrl,
          children: [
            {
              name: "",
              url: "",
              key: "reports",
              children: snapProgramLinks
            }
          ]
        }
        products.push(snapReportsTab)
      }
    }
  }

  const eBooks = {
    name: "eBooks",
    url: "",
    key: "ebooks",
    children: []
  }

  const eBooksLicenses = licenses
    .filter(license => {
      let isEbookLicense = license.navCategory === "eBooks"
      if (
        license.navCategory === "Mastery Resources" &&
        license.navName.includes("EACT Curriculum")
      ) {
        isEbookLicense = true
      }
      if (
        (Roles.Teacher === user.role || Roles.Instructor === user.role) &&
        isEbookLicense
      ) {
        const isLicenseActive =
          Object.values(license?.licensedUsers ?? {}).length &&
          Number(license.endDate) > Date.now()
        return isLicenseActive
      } else if (
        Roles.Sales_Rep !== user.role &&
        Roles.Super_Administrator !== user.role &&
        isEbookLicense
      ) {
        return !license.isExpired
      }
      return isEbookLicense
    })
    .sort((a, b) => {
      return a.position - b.position
    })

  const eBooksLink = eBooksLicenses
    .map(license => {
      const name = license.navName
      const program = getProgramByLicenseName(license.navName || license.name)
      let url = ""

      if (program) {
        url = `/ebooks/${program}/${params.ebookId ?? ""}`
      }

      if (!queryObj.show_by) {
        queryObj.show_by = role === Roles.Student ? "students" : "all"
      }

      if (Object.keys(queryObj).length) {
        url = `${url}?${queryString.stringify(queryObj)}`
      }
      return {
        name,
        url: url,
        key: program
      }
    })
    .filter(e => e.key)

  if (eBooksLink.length) {
    eBooks.url =
      eBooksLink.find(link => link.key === currentProgram)?.url ??
      eBooksLink[0].url ??
      "/ebooks"
    eBooks.children = [
      {
        name: "",
        url: "",
        key: "ebooks",
        children: orderBy(uniqBy(eBooksLink, "key"), ["key"], ["asc"])
      }
    ]
  }

  const manage = {
    name: "Manage",
    url: "/manage",
    key: "manage",
    children: []
  }

  const contentAuthoring = {
    name: "Content Authoring",
    url: "/snap/author/mastery-lessons",
    key: "author",
    children: []
  }

  /**
   * @deprecated DEPRECATED_MANAGE_ORG_NAV — consumes the old `/manage/organization` user-management nav.
   * Remove or repoint to `/reporting/organization` when that nav is deleted.
   */
  const organizationLinks = getManageUsersSidebarLinks(
    user,
    schools,
    educators,
    location
  )?.map(item => ({
    name: item.name,
    url: item.link,
    key: item.name.toLowerCase().split(" ").join("-")
  }))

  if (organizationLinks.length && role !== Roles.Course_Author) {
    manage.children.push({
      name: "Organization",
      url: organizationLinks[0]?.url || "",
      key: "organization"
    })
  }

  if (role === Roles.Super_Administrator) {
    manage.children.push(
      {
        name: "Licenses",
        url: "/manage/licenses/districts",
        key: "licenses",
        children: []
      },
      {
        name: "Content",
        url: "/manage/content/manage",
        key: "content",
        children: []
      }
    )

    const reports = {
      name: "Reports",
      url: `/reports/participation-report?${queryString.stringify(queryObj)}`,
      key: "reports",
      children: [
        {
          name: "Participation Report",
          url: `/reports/participation-report?${queryString.stringify(
            queryObj
          )}`,
          key: "participation-report",
          children: []
        },
        {
          name: "Usage Report",
          url: `/reports/usage-report?${queryString.stringify(queryObj)}`,
          key: "usage-report",
          children: []
        }
      ]
    }

    if (queryObj.show_by === "all") {
      delete queryObj.show_by
    }

    // School Support supports only ACT program
    const actTruscoreTesting =
      truscoreTesting.filter(item => item.key === "act") || []
    const firstSchoolSupportTesting = actTruscoreTesting[0]

    const getSchoolSupportTestingQueryObj = assessmentId => {
      let newQueryObj = {
        ...queryObj,
        y: getSchoolYear().year
      }

      if (!!assessmentId) {
        newQueryObj = {
          ...newQueryObj,
          a: assessmentId
        }
      }
      return queryString.stringify(newQueryObj)
    }

    const schoolSupport = {
      name: "School Support",
      url: `/school-support?${queryString.stringify(queryObj)}`,
      key: "school-support",
      children: [
        {
          name: "Test Administration",
          url: `/school-support/test-administration${
            !!firstSchoolSupportTesting
              ? `/${firstSchoolSupportTesting.key}/answer-document-upload/${firstSchoolSupportTesting.id}`
              : ""
          }?${getSchoolSupportTestingQueryObj(firstSchoolSupportTesting?.id)}`,
          key: "test-administration",
          children: actTruscoreTesting.map(link => {
            return {
              ...link,
              url: `/school-support/test-administration/${
                link?.key
              }/answer-document-upload/${
                link?.id
              }?${getSchoolSupportTestingQueryObj(link?.id)}`
            }
          })
        }
      ]
    }

    const contextHelpManager = {
      name: "Context Help Manager",
      url: "/context-help-manager",
      key: "context-help-manager",
      children: [
        {
          key: "announcements",
          name: "Announcements",
          url: "/context-help-manager/announcements"
        },
        {
          key: "tips",
          name: "Tips",
          url: "/context-help-manager/tips"
        },
        {
          key: "help-boxes",
          name: "Help Boxes",
          url: "/context-help-manager/help-boxes"
        }
      ]
    }

    return {
      products,
      eBooks,
      manage,
      contentAuthoring,
      reports,
      schoolSupport,
      contextHelpManager
    }
  } else if (role === Roles.Sales_Rep) {
    if (queryObj.show_by === "all") {
      delete queryObj.show_by
    }

    const reports = {
      name: "Reports",
      url: `/reports/usage-report?${queryString.stringify(queryObj)}`,
      key: "reports",
      children: [
        {
          name: "Usage Report",
          url: `/reports/usage-report?${queryString.stringify(queryObj)}`,
          key: "usage-report",
          children: []
        },
        {
          name: "Participation Report",
          url: `/reports/participation-report?${queryString.stringify(
            queryObj
          )}`,
          key: "participation-report",
          children: []
        }
      ]
    }

    return {
      products,
      eBooks,
      reports
    }
  } else if (role === Roles.Course_Author) {
    manage.children.push({
      name: "Content",
      url: "/manage/content/manage",
      key: "content",
      children: []
    })

    return {
      manage,
      contentAuthoring
    }
  } else if (
    role === Roles.District_Admin ||
    role === Roles.ACT_Admin ||
    role === Roles.ACT_Multi_District_Admin ||
    role === Roles.School_Admin
  ) {
    const reports = {
      name: "Reports",
      key: "reports",
      children: []
    }

    reports.children.push({
      name: "Usage Report",
      url: `/reports/usage-report?${queryString.stringify(queryObj)}`,
      key: "usage-report",
      children: []
    })

    if (truScoreProducts.url) {
      reports.children.push({
        name: "Participation Report",
        url: `/reports/participation-report?${queryString.stringify(queryObj)}`,
        key: "participation-report",
        children: []
      })

      reports.url = reports.children[0].url
    }

    if (
      role === Roles.District_Admin ||
      role === Roles.ACT_Admin ||
      role === Roles.ACT_Multi_District_Admin
    ) {
      manage.children.push({
        name: "Platform Configuration",
        url: "/manage/platform-configuration",
        key: "platform-configuration",
        children: []
      })
    }

    return {
      products,
      eBooks,
      manage,
      reports
    }
  } else if (role === Roles.Teacher || role === Roles.Instructor) {
    return {
      products,
      eBooks,
      manage
    }
  } else if (role === Roles.Student) {
    return {
      products,
      eBooks
    }
  }
}

const getAssessmentLink = (
  params,
  licenseId,
  query,
  assesmentSets,
  isBubbleSheet,
  program,
  checkProgramType
) => {
  const { subtestId, subPage, module } = params
  let currentSubtestId = ""

  if (query.g) {
    delete query.g
  }
  if (query.item) {
    delete query.item
  }

  if (module != "study-hall" && query.watch_video) {
    delete query.watch_video
  }

  if (subtestId && !assesmentSets && subPage === "test-taking") {
    return
  }

  if (
    assesmentSets &&
    Object.keys(assesmentSets).length &&
    licenseId &&
    subtestId
  ) {
    let activeSubtest = {}
    for (const assessmentId in assesmentSets) {
      if (assesmentSets[assessmentId]?.assessmentSubtests?.[subtestId]) {
        activeSubtest = {
          ...assesmentSets[assessmentId]?.assessmentSubtests?.[subtestId],
          assessmentId
        }
      }
    }

    if (
      activeSubtest &&
      subPage === "test-taking" &&
      checkProgramType(program?.toUpperCase(), "adaptive")
    ) {
      return `/truscore/${
        isBubbleSheet ? "bubblesheets" : "practice-tests"
      }/${program}/${subPage}/${licenseId}/${subtestId}?${queryString.stringify(
        {
          ...query,
          a: licenseId
        }
      )}`
    }

    if (activeSubtest) {
      if (assesmentSets[licenseId]) {
        for (const currSubtestId in assesmentSets?.[licenseId]
          ?.assessmentSubtests) {
          if (
            assesmentSets[licenseId].assessmentSubtests[currSubtestId]
              .displayName === activeSubtest.displayName &&
            assesmentSets[licenseId].assessmentSubtests[currSubtestId].setId !==
              activeSubtest.setId
          ) {
            currentSubtestId = currSubtestId
          } else {
            let key = null
            for (const k in assesmentSets[licenseId].assessmentSet.content) {
              if (
                assesmentSets[licenseId].assessmentSet.content[k].index === 0
              ) {
                key = k
              }
            }
            if (key) {
              currentSubtestId =
                assesmentSets[licenseId].assessmentSubtests[key].setId
            }
          }
        }
      }
    }
  }

  if (
    ["skill-report", "item-report", "detail-report", "test-taking"].includes(
      subPage
    ) &&
    currentSubtestId
  ) {
    return `/truscore/${
      isBubbleSheet ? "bubblesheets" : "practice-tests"
    }/${program}/${subPage}/${licenseId}/${currentSubtestId}?${queryString.stringify(
      {
        ...query,
        a: licenseId
      }
    )}`
  }

  delete query.compare_id
  delete query.compare_year

  return `/truscore/${
    isBubbleSheet ? "bubblesheets" : "practice-tests"
  }/${program}/overview/${licenseId}?${queryString.stringify({
    ...query,
    a: licenseId,
    y: getSchoolYear().year
  })}`
}
